// AlgoliaAutocomplete.tsx
// Main component for integrating Algolia Autocomplete into the product list UI.
// Handles search box, plugins, and panel rendering for product search suggestions.

import type { BaseItem } from '@algolia/autocomplete-core';
import type { AutocompleteOptions } from '@algolia/autocomplete-js';
import { createElement, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { useHierarchicalMenu, usePagination, useSearchBox } from 'react-instantsearch';
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import { debounce } from '@algolia/autocomplete-shared';
import './autocomplete.css';
import '@algolia/autocomplete-theme-classic';
import { searchClient } from 'algolia/searchClient';
import AlgoliaSearchSuggestion from '../search-header/algoliaSearchSuggestion';
import { createLocalStorageRecentlyViewedItems } from './recentlyViewedItems';
import { createQuerySuggestionsPluginReact } from './querySuggestionsPlugin';
import { createQuerySuggestionsInCategoryPlugin } from './querySuggestionsInCategoryPlugin';
import { getLocalizedField, productUrl } from 'helpers/product/productUrl';
import useLocale from 'helpers/hooks/useLocale';
import { Hit } from 'algoliasearch';
import { INSTANT_SEARCH_HIERARCHICAL_ATTRIBUTES } from '../../context/constants';
import { createAlgoliaRedirectUrlPlugin } from './redirectUrlPlugin';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mediumDesktop } from 'helpers/utils/screensizes';
import Link from 'next/link';
import results from 'public/locales/en/product.json';
import { AlgoliaAnalytics } from 'helpers/AlgoliaEvents';
import { useAccount } from 'frontastic';
import { CLICKED_OBJECT_ID, SEARCH_QUERY_ID } from 'helpers/constants/algolia';

type AutocompleteProps = Partial<AutocompleteOptions<BaseItem>> & {
  searchClient?: any;
  className?: string;
};

type SetInstantSearchUiStateOptions = {
  query: string;
  category?: string;
};
const client = searchClient;
export function AlgoliaAutocomplete({ searchClient = client, className, ...autocompleteProps }: AutocompleteProps) {
  const autocompleteContainer = useRef<HTMLDivElement>(null);
  const panelRootRef = useRef<Root | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null); // Add input ref
  const { loggedIn, result } = useAccount();
  const handleClickAfterSearchResult = (objectID: string, queryId: string, position: number) => {
    const userId = loggedIn
      ? result?.data?.data?.account?.accountId
      : typeof window !== 'undefined' && localStorage.getItem('__anon_id');
    const analytics = new AlgoliaAnalytics(locale);
    if (queryId && position) analytics.handleClickAfterSearch([objectID], userId, queryId, [position], true);
    if (objectID) sessionStorage.setItem(CLICKED_OBJECT_ID, objectID);
    if (queryId) sessionStorage.setItem(SEARCH_QUERY_ID, queryId);
  };

  const hasRedirectedRef = useRef(false);
  const hasSubmittedRef = useRef(false);
  const hasStateChangedRef = useRef(false);
  const { locale, fallbackLocale, localeKey, getCurrency } = useLocale();

  let INSTANT_SEARCH_INDEX_NAME: string = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_US ?? '';
  let INSTANT_SEARCH_QUERY_SUGGESTIONS_INDEX_NAME: string =
    process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_QUERY_SUGGESTIONS_INDEX_US ?? '';
  if (getCurrency() !== 'GBP') {
    INSTANT_SEARCH_INDEX_NAME = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_DE ?? INSTANT_SEARCH_INDEX_NAME;
    INSTANT_SEARCH_QUERY_SUGGESTIONS_INDEX_NAME =
      process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_QUERY_SUGGESTIONS_INDEX_DE ??
      INSTANT_SEARCH_QUERY_SUGGESTIONS_INDEX_NAME;
  } else if (locale === 'en' || locale === 'en_GB') {
    INSTANT_SEARCH_INDEX_NAME = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_US ?? INSTANT_SEARCH_INDEX_NAME;
    INSTANT_SEARCH_QUERY_SUGGESTIONS_INDEX_NAME =
      process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_QUERY_SUGGESTIONS_INDEX_US ??
      INSTANT_SEARCH_QUERY_SUGGESTIONS_INDEX_NAME;
  } else {
    INSTANT_SEARCH_INDEX_NAME = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_DE ?? INSTANT_SEARCH_INDEX_NAME;
    INSTANT_SEARCH_QUERY_SUGGESTIONS_INDEX_NAME =
      process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_QUERY_SUGGESTIONS_INDEX_DE ??
      INSTANT_SEARCH_QUERY_SUGGESTIONS_INDEX_NAME;
  }

  const { query, refine: setQuery } = useSearchBox();
  const { items: categories, refine: setCategory } = useHierarchicalMenu({
    attributes: INSTANT_SEARCH_HIERARCHICAL_ATTRIBUTES,
  });
  const { refine: setPage } = usePagination();

  const [instantSearchUiState, setInstantSearchUiState] = useState<SetInstantSearchUiStateOptions>({ query });
  const debouncedSetInstantSearchUiState = debounce(setInstantSearchUiState, 5000);

  useEffect(() => {
    setQuery(instantSearchUiState?.query);
    if (instantSearchUiState?.category) {
      setCategory(instantSearchUiState?.category);
    }
    setPage(0);
  }, [instantSearchUiState]);

  const currentCategory = useMemo(() => {
    const found = categories?.find(({ isRefined }) => isRefined)?.value;
    return found;
  }, [categories]);

  const recentlyViewedItems = useMemo(
    () =>
      createLocalStorageRecentlyViewedItems({
        key: 'RECENTLY_VIEWED',
        limit: 10,
      }),
    [],
  );

  const querySuggestionsPlugin = useMemo(() => {
    return createQuerySuggestionsPluginReact({
      searchClient,
      indexName: INSTANT_SEARCH_QUERY_SUGGESTIONS_INDEX_NAME,
      currentCategory,
      setInstantSearchUiState,
    });
  }, [searchClient, currentCategory, setInstantSearchUiState]);

  const querySuggestionsInCategoryPlugin = useMemo(() => {
    return createQuerySuggestionsInCategoryPlugin({
      searchClient,
      indexName: INSTANT_SEARCH_INDEX_NAME,
      currentCategory,
      setInstantSearchUiState,
    });
  }, [searchClient, currentCategory, setInstantSearchUiState]);

  const redirectUrlPlugin = useMemo(() => createAlgoliaRedirectUrlPlugin(), []);
  const plugins = useMemo(() => {
    const arr = [
      redirectUrlPlugin,
      //  recentlyViewedItems,
      //  querySuggestionsPlugin,
      //   querySuggestionsInCategoryPlugin
    ];
    return arr;
  }, [
    redirectUrlPlugin,
    // recentlyViewedItems, querySuggestionsPlugin, querySuggestionsInCategoryPlugin
  ]);

  useEffect(() => {
    if (!autocompleteContainer?.current) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const inputContainer = autocompleteContainer?.current;
      const panelNode = document?.querySelector('.custom-autocomplete-container');
      const aaPanelLayout = document?.querySelector('.aa-PanelLayout');
      if (
        (!inputContainer || !inputContainer?.contains(event?.target as Node)) &&
        (!panelNode || !panelNode?.contains(event?.target as Node)) &&
        (!aaPanelLayout || !aaPanelLayout?.contains(event?.target as Node))
      ) {
        if ((event?.target as HTMLElement)?.closest('a')) {
          return;
        }
        // Close the panel if open
        if (autocompleteInstance && typeof autocompleteInstance?.setIsOpen === 'function') {
          autocompleteInstance?.setIsOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    const autocompleteInstance = autocomplete({
      ...autocompleteProps,
      container: autocompleteContainer?.current,
      initialState: { query },
      insights: false,
      plugins,
      openOnFocus: true,
      onReset() {
        setInstantSearchUiState({ query: '', category: currentCategory });
      },
      onSubmit({ state }) {
        hasSubmittedRef.current = true;
        setInstantSearchUiState({ query: state?.query });
        if (hasStateChangedRef?.current) {
          handleAlgoliaRedirect({ state, hasRedirectedRef, hasStateChangedRef });
        }
      },

      onStateChange({ prevState, state }) {
        if (prevState?.query !== state?.query) {
          hasStateChangedRef.current = true;
          debouncedSetInstantSearchUiState({ query: state?.query });
        }

        if (!hasSubmittedRef?.current) {
          return;
        }
        handleAlgoliaRedirect({ state, hasRedirectedRef, hasStateChangedRef });
      },

      renderer: { createElement, Fragment, render: () => {} },
      render({ children }, root) {
        if (!panelRootRef?.current || rootRef?.current !== root) {
          rootRef.current = root;

          panelRootRef?.current?.unmount();
          panelRootRef.current = createRoot(root);
        }

        panelRootRef?.current?.render(children);
      },
      getSources({ query }) {
        if (!query || query?.trim() === '') return [];
        return [
          {
            sourceId: 'products',
            getItems() {
              if (!query) return [];
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: INSTANT_SEARCH_INDEX_NAME,
                    params: {
                      query,
                      hitsPerPage: 20,
                      attributesToSnippet: ['name:10', 'description:35'],
                      snippetEllipsisText: '…',
                    },
                  },
                ],
              });
            },
            onSelect({ item }: { item: BaseItem }) {
              if (item?.__autocomplete_id && item?.__autocomplete_queryID && item?.objectID) {
                handleClickAfterSearchResult(
                  typeof item?.objectID === 'string' ? item?.objectID : '',
                  typeof item?.__autocomplete_queryID === 'string' ? item?.__autocomplete_queryID : '',
                  typeof item?.__autocomplete_id === 'number' ? item?.__autocomplete_id : 0,
                );
              }
              if (!recentlyViewedItems?.data || !item) return {};
              const hit = item as {
                variantCode: any;
                variants: any;
                objectID?: string;
                name?: Record<string, string>;
                images?: string[];
                slug?: Record<string, string>;
              };
              const name = getLocalizedField(hit?.name, localeKey, fallbackLocale);
              let slug = productUrl(item as Hit<any>, localeKey, fallbackLocale);
              slug = slug + `?variantCode=${hit?.variantCode}&sku=${hit?.variants?.[0]?.sku || ''}`;
              recentlyViewedItems?.data?.addItem({
                id: String(hit?.objectID ?? ''),
                label: name,
                image: String(hit?.images?.[0] ?? ''),
                url: slug,
              });
              // Focus the input after selection
              inputRef?.current?.focus();
              if (item && slug) {
                window.location.href = slug;
              }
            },
            templates: {
              // header({ state, items }) {
              //   if (!state?.query || items?.length === 0) return '';
              //   return (
              //     <Fragment>
              //       <span className="aa-SourceHeaderTitle">Products</span>
              //       <div className="aa-SourceHeaderLine" />
              //     </Fragment>
              //   );
              // },
              footer({ state, items }) {
                if (!state?.query || items?.length === 0) return '';
                return (
                  <div className="flex justify-center px-5 py-10">
                    <Link href={`/search/${state?.query}`}>
                      <div className="cursor-pointer text-sm underline">{results?.algoliaResults}</div>
                    </Link>
                  </div>
                );
              },
              item({ item }: { item: BaseItem; components: any; html: any; state: any }) {
                return (
                  <div>
                    <AlgoliaSearchSuggestion
                      isSearchPage={false}
                      matchingProduct={null}
                      onSuggestionClick={() => {}}
                      queryString=""
                      searchConfiguration={() => {}}
                      hits={{ hit: item }}
                    />
                  </div>
                );
              },
              noResults({ state }) {
                if (!state?.query) return '';
                return (
                  <div className="flex justify-center px-5 py-10">
                    <Link href={`/search/${state?.query}`}>
                      <div className="cursor-pointer text-sm underline">{results?.algoliaResults}</div>
                    </Link>
                  </div>
                );
              },
            },
          },
        ];
      },
    });

    return () => {
      document?.removeEventListener('mousedown', handleClickOutside);
      autocompleteInstance && autocompleteInstance?.destroy();
    };
  }, [plugins]);

  // Animated placeholder logic
  const productsList = [
    'Titleist SM10 Wedge',
    'Golf Midlayers',
    'New Balance',
    'Range Finder',
    'Titleist Onyx Collection',
    'BOSS',
    'Callaway Ai Smoke',
    'Wilson',
    'Tees',
    'Callaway Mavrik',
    'Taylor Made Qi10',
    'Umbrella',
    'Stromberg',
  ];
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [pauseBeforeNext, setPauseBeforeNext] = useState(true);
  const [placeholder, setPlaceholder] = useState('Search American Golf...');
  const [isDesktop] = useMediaQuery(mediumDesktop);
  const [isFocused, setIsFocused] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const isSearchPage = false; // Set this based on your routing if needed

  useEffect(() => {
    if (isSearchPage) {
      setPlaceholder('');
      return;
    }
    if (isFocused || pauseBeforeNext || isSearching) {
      return;
    }
    const product = productsList?.[currentProductIndex];
    const interval = setInterval(() => {
      if (charIndex <= product?.length) {
        setPlaceholder(`${product?.substring(0, charIndex)}`);
        setCharIndex(charIndex + 1);
      } else {
        clearInterval(interval);
        setCharIndex(0);
        setCurrentProductIndex((currentProductIndex + 1) % productsList?.length);
        setPauseBeforeNext(true);
      }
    }, 180);

    return () => clearInterval(interval);
  }, [isFocused, currentProductIndex, charIndex, pauseBeforeNext, isSearchPage]);

  useEffect(() => {
    if (pauseBeforeNext && !isSearchPage) {
      setPlaceholder('Search American Golf...');
      const timeout = setTimeout(() => {
        setPauseBeforeNext(false);
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [pauseBeforeNext]);

  // Set placeholder on Algolia input
  useEffect(() => {
    const container = autocompleteContainer?.current;
    if (!container) return;
    const input = container?.querySelector('.aa-Input');
    if (input && !isFocused) {
      input?.setAttribute('placeholder', placeholder);
    }
    // Optionally clear placeholder on focus
    if (input) {
      input?.addEventListener('focus', () => setIsFocused(true));
      input?.addEventListener('blur', () => setIsFocused(false));
    }
    return () => {
      if (input) {
        input?.removeEventListener('focus', () => setIsFocused(true));
        input?.removeEventListener('blur', () => setIsFocused(false));
      }
    };
  }, [placeholder, isFocused]);

  return <div className={className} ref={autocompleteContainer} />;
}

export function handleAlgoliaRedirect({
  state,
  hasRedirectedRef,
  hasStateChangedRef,
}: {
  state: any;
  hasRedirectedRef: React.MutableRefObject<boolean>;
  hasStateChangedRef: React.MutableRefObject<boolean>;
}) {
  const q = state?.query?.trim() ?? '';
  if (!q) return;

  const hits = state?.collections?.[0]?.items ?? [];
  const redirectData = (state?.context?.redirectUrlPlugin as any)?.data ?? [];
  const productEntry = Array?.isArray(redirectData) ? redirectData?.find((d) => d?.sourceId === 'products') : null;
  const pluginRedirectUrl = productEntry?.urls?.[0];

  const isGiftQuery =
    q === '9988004001005' ||
    q === '9988003001006' ||
    q?.toLowerCase()?.includes('voucher') ||
    q?.toLowerCase()?.includes('gift');

  if (
    (!hasRedirectedRef?.current && (hits?.length > 0 || pluginRedirectUrl || isGiftQuery)) ||
    hasStateChangedRef?.current
  ) {
    hasRedirectedRef.current = true;
    let target: string;
    if (isGiftQuery) {
      target = '/services/giftcards-evouchers.html';
    } else if (pluginRedirectUrl) {
      const isExternal = /^(\/\/|https?:\/\/|www\.)/.test(pluginRedirectUrl);
      target = isExternal ? pluginRedirectUrl : window?.location?.origin + pluginRedirectUrl;
    } else {
      target = `/search/${encodeURIComponent(q)}`;
    }
    window.location.href = target;
  }
}
